import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../Common/RichTextEditor';

const schema = yup.object({
  title: yup.string().required('Title is required').max(200, 'Title must be less than 200 characters'),
  heading: yup.string().required('Heading is required').max(200, 'Heading must be less than 200 characters'),
  subtitle: yup.string().required('Subtitle is required').max(200, 'Subtitle must be less than 200 characters'),
  description: yup.string().required('Description is required').max(500, 'Description must be less than 500 characters'),
  linkUrl: yup.string().required('Link URL is required').url('Must be a valid URL').max(500, 'URL must be less than 500 characters'),
});

const HeroContentModal = ({ isOpen, onClose, onSubmit, heroContent }) => {
  const isEdit = Boolean(heroContent && !heroContent.viewMode);
  const isViewMode = Boolean(heroContent?.viewMode);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      heading: '',
      subtitle: '',
      description: '',
      linkUrl: '',
    },
  });

  useEffect(() => {
    if (heroContent) {
      reset({
        title: heroContent.title || '',
        heading: heroContent.heading || heroContent.main_heading || '',
        subtitle: heroContent.subtitle || heroContent.sub_heading || '',
        description: heroContent.description || '',
        linkUrl: heroContent.link_url || heroContent.link || '',
      });
      
      if (heroContent.image?.url || heroContent.image_url) {
        setImagePreview(heroContent.image?.url || heroContent.image_url);
      }
      setRemoveExistingImage(false);
    } else {
      reset({
        title: '',
        heading: '',
        subtitle: '',
        description: '',
        linkUrl: '',
      });
      setImageFile(null);
      setImagePreview(null);
      setRemoveExistingImage(false);
      // Clear file input when modal resets
      const fileInput = document.getElementById('image-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }, [heroContent, reset]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setRemoveExistingImage(false); // Reset remove flag when new image is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setImageFile(null);
    setImagePreview(null);
    setRemoveExistingImage(true); // Flag that existing image should be removed
    // Clear the file input element
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFormSubmit = async (data) => {
    try {
      // For create mode: Image is mandatory
      // For edit mode: Image is optional (can keep existing, remove, or replace)
      if (!isEdit && !imageFile && !imagePreview) {
        alert('Image is required for hero content. Please upload an image.');
        return;
      }

      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('heading', data.heading); // Required
      formData.append('subtitle', data.subtitle || '');
      formData.append('description', data.description || '');
      formData.append('link', data.linkUrl || '#'); // Required
      formData.append('linkUrl', data.linkUrl || '#');
      formData.append('date', new Date().toISOString()); // Required
      
      // Handle image upload/removal
      if (imageFile) {
        // New image file selected (this takes priority over removeImage flag)
        formData.append('coverImage', imageFile); // Backend expects 'coverImage'
        // Don't send removeImage flag if we're uploading a new image
      } else if (removeExistingImage && isEdit) {
        // User clicked X to remove existing image (only in edit mode)
        formData.append('removeImage', 'true');
      }
      // If neither imageFile nor removeExistingImage, keep existing image (for edit mode)
      
      await onSubmit(formData);
      setImageFile(null);
      setImagePreview(null);
      setRemoveExistingImage(false);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel onClick={(e) => e.stopPropagation()} className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {isViewMode ? 'View Hero Content' : isEdit ? 'Edit Hero Content' : 'Create New Hero Content'}
                    </Dialog.Title>
                    
                    <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                            Title *
                          </label>
                          <input
                            {...register('title')}
                            type="text"
                            disabled={isViewMode}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Enter title"
                          />
                          {errors.title && (
                            <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label htmlFor="heading" className="block text-sm font-medium text-gray-700">
                            Main Heading *
                          </label>
                          <input
                            {...register('heading')}
                            type="text"
                            disabled={isViewMode}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Enter main heading"
                          />
                          {errors.heading && (
                            <p className="mt-1 text-sm text-red-600">{errors.heading.message}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700">
                            Subtitle *
                          </label>
                          <input
                            {...register('subtitle')}
                            type="text"
                            disabled={isViewMode}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Enter subtitle"
                          />
                          {errors.subtitle && (
                            <p className="mt-1 text-sm text-red-600">{errors.subtitle.message}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
                          </label>
                          <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                              <RichTextEditor
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Enter description"
                                height="150px"
                                error={errors.description?.message}
                                disabled={isViewMode}
                              />
                            )}
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label htmlFor="linkUrl" className="block text-sm font-medium text-gray-700">
                            Link URL *
                          </label>
                          <input
                            {...register('linkUrl')}
                            type="url"
                            disabled={isViewMode}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="https://example.com"
                          />
                          {errors.linkUrl && (
                            <p className="mt-1 text-sm text-red-600">{errors.linkUrl.message}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Hero Image <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">📐 Image Guidelines</h4>
                          <ul className="text-xs text-blue-700 space-y-1">
                            <li>• <strong>Recommended Size:</strong> 1920 x 600 pixels (16:5 aspect ratio)</li>
                            <li>• <strong>Minimum Size:</strong> 1200 x 400 pixels</li>
                            <li>• <strong>File Types:</strong> PNG, JPG, JPEG, WebP, GIF (static or animated)</li>
                            <li>• <strong>Max File Size:</strong> 10MB</li>
                            <li>• <strong>Position:</strong> Image will be displayed as full-width hero banner</li>
                            <li>• <strong>Caching:</strong> Images are cached for 24 hours for performance</li>
                            <li>• <strong>Video:</strong> Use Link URL field to embed YouTube/Vimeo videos</li>
                          </ul>
                        </div>
                        {imagePreview ? (
                          <div className="mt-2 relative">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-48 object-cover rounded-md"
                            />
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeImage(e);
                                }}
                                className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : !isViewMode ? (
                          <div 
                            className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-400 transition-colors cursor-pointer"
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-500', 'bg-primary-50'); }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50'); }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
                              const file = e.dataTransfer.files[0];
                              if (file && file.type.startsWith('image/')) {
                                setImageFile(file);
                                setRemoveExistingImage(false); // Reset remove flag
                                const reader = new FileReader();
                                reader.onloadend = () => setImagePreview(reader.result);
                                reader.readAsDataURL(file);
                              }
                            }}
                          >
                            <div className="space-y-1 text-center">
                              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <div className="flex text-sm text-gray-600">
                                <label
                                  htmlFor="image-upload"
                                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500"
                                >
                                  <span>Upload a file</span>
                                  <input
                                    id="image-upload"
                                    name="image-upload"
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                    className="sr-only"
                                    onChange={handleImageChange}
                                  />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                              </div>
                              <p className="text-xs text-gray-500">PNG, JPG, WebP, GIF up to 10MB</p>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md bg-gray-50">
                            <div className="space-y-1 text-center">
                              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                              <p className="text-sm text-gray-500">No image uploaded</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        {!isViewMode && (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:col-start-2 disabled:opacity-50"
                          >
                            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                          </button>
                        )}
                        <button
                          type="button"
                          className={`mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 ${isViewMode ? 'sm:col-span-2' : 'sm:col-start-1'}`}
                          onClick={onClose}
                        >
                          {isViewMode ? 'Close' : 'Cancel'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default HeroContentModal;
